// Lyra(궁수) 임시 외형(c022 D4a): Lyra 전용 3D 모델(Tripo → Mixamo, PC 게이트)이 오기 전까지 Kael 리그·클립을 그대로 쓰고
// 그 위에 (a) 대검을 숨기고 왼손에 절차 장궁(시위 당김 표현 포함), 등에 화살통, (b) 청록 #3fd0c0 + 은 #c8d2dc 색조(옷의 진홍만 청록으로,
// 외곽선·가림 실루엣도 청록 계열), (c) 머리 장식(은빛 깃 두 개)으로 실루엣을 바꾼다.
// 순수 함수 API: applyLyraLook(kaelRoot) → LyraLook 핸들, removeLyraLook(핸들)로 원래대로. Kael 렌더 코드는 바꾸지 않는다(Kael에 영향 0).
// 무기 소켓: Mixamo 본 이름 끝(…LeftHand, …RightHand, …Spine2, …Head)으로 찾는다(GLTFLoader가 'mixamorig:'의 ':'를 지워도 끝은 같다).
// 주의: KaelModel.setTier가 몸 메시·재질을 새로 만들면 색조가 빠진다 → 단계 교체 뒤 removeLyraLook → applyLyraLook을 다시 부른다.
// 프레임마다: setLyraDraw(핸들, 0..1)로 시위를 당긴다(할당 없음). 드로우콜: 활 2(몸·외곽선) + 시위 2 + 화살통 2 + 머리 장식 1 = 7.
import * as THREE from 'three/webgpu';
import { BOW_GLB_LENGTH, BOW_SOCKET, bowTips } from './heroWeapons';
import {
  float, materialColor, max, mix, normalLocal, normalView, positionLocal, positionViewDirection, smoothstep, step, vec3,
} from 'three/tsl';

export const LYRA_TEAL = 0x3fd0c0;
export const LYRA_SILVER = 0xc8d2dc;
/** 외곽선(반전 헐) 색: 어두운 청록(Kael 0x0b0f1c 남색 대신) */
export const LYRA_OUTLINE = 0x06302e;
/** 장비 외곽선 두께(m, 소켓에서 본 배율을 상쇄한 월드 단위) */
const GEAR_OUTLINE_M = 0.009;

export interface LyraLookOptions {
  /**
   * 몸: 'kael'(카엘 몸 + 라이라 색조, 기본) | 'lyra'(라이라 전용 몸). 활 소켓 회전·위치·길이·시위 당김 깊이의 기본값을 몸별로 고른다(BOW_SOCKET).
   * bowRot·bowPos·bowRatio·drawReach를 따로 주면 그 값이 이긴다
   */
  body?: 'kael' | 'lyra';
  /** 활 길이(캐릭터 키 대비 비율). 기본은 몸별 BOW_SOCKET.ratio */
  bowRatio?: number;
  /** 가득 당겼을 때 시위 중앙이 물러나는 길이(활 길이 비율). 기본은 몸별 BOW_SOCKET.reach */
  drawReach?: number;
  /** 왼손 소켓 회전(도, XYZ). 손 본 축 기준(Mixamo: Y = 손가락 방향) */
  bowRot?: readonly [number, number, number];
  /** 왼손 소켓 위치(m, 손 본 기준) */
  bowPos?: readonly [number, number, number];
  /** 색조를 몸에 적용할지(기본 true) */
  tint?: boolean;
  /** false면 절차 화살통·머리 장식을 숨긴다(라이라 전용 몸은 모델에 화살통·머리가 있다). 기본 true */
  accessories?: boolean;
  /** 장비 그림자 */
  castShadow?: boolean;
  /**
   * true면 bowRot 대신 지금 자세에서 활대가 월드 위(+Y), 화살 쪽이 영웅 정면(루트 +Z)을 향하도록 소켓 회전을 계산한다
   * (기본값 LYRA_BOW_ROT는 Kael idle 0.5초 자세에서 이 계산으로 구해 굳힌 값)
   */
  autoAlign?: boolean;
  /** autoAlign 때 영웅 공간에서 활을 더 돌리는 각(도, XYZ) */
  alignEuler?: readonly [number, number, number];
  /**
   * Blender 생성 장궁(heroWeapons.ts, 정점 색, 길이 BOW_GLB_LENGTH m, 같은 활 공간). 주면 절차 활 대신 이 모양을 L에 맞춰 늘려 쓴다.
   * 활 끝(시위 고정점)은 같은 비율(bowTips)이다.
   */
  bowGeometry?: THREE.BufferGeometry;
}

/**
 * 왼손 본 기준 장궁 기본 회전(도): Kael idle 0.5초 자세에서 autoAlign + alignEuler [0, 90, -30]으로 계산해 굳힌 값.
 * 활을 몸 앞에 비스듬히 든 쉼 자세(활대 30° 기울임, 활 면이 정면·옆 카메라 모두에서 휜 모양으로 보인다). qa/c022-lyra/vfx/look_*.png
 */
export const LYRA_BOW_ROT: readonly [number, number, number] = BOW_SOCKET.kael.rot;
export const LYRA_BOW_POS: readonly [number, number, number] = BOW_SOCKET.kael.pos;

export interface LyraLook {
  readonly root: THREE.Object3D;
  readonly bow: THREE.Group;
  readonly quiver: THREE.Group;
  readonly ornament: THREE.Group;
  /** 활 소켓(왼손 본 아래) */
  readonly bowSocket: THREE.Object3D;
  /** 숨긴 대검 소켓들과 원래 visible */
  readonly hidden: readonly { obj: THREE.Object3D; visible: boolean }[];
  /** 바꾼 재질과 원래 값(복원용) */
  readonly restore: readonly (() => void)[];
  /** 시위 당김(0..1) */
  draw: number;
  /** 시위 중간(당김점) 국소 좌표·끝점(활 공간) */
  readonly tipTop: THREE.Vector3;
  readonly tipBottom: THREE.Vector3;
  readonly nock: THREE.Vector3;
  readonly strings: readonly [THREE.Mesh, THREE.Mesh];
  /** 가득 당길 때 시위 중앙이 물러나는 길이(활 길이 비율) */
  readonly reach: number;
  /** 소켓 회전 두 개: 쏘는 자세와 들고 있는 자세(카엘 몸은 같다). setLyraAim이 섞는다 */
  readonly aimQ: THREE.Quaternion;
  readonly carryQ: THREE.Quaternion;
  /** 쏘는 자세 비중(0 들기 ~ 1 쏘기) */
  aim: number;
  /** 캐릭터 키(m, 적용 때 잰 값) */
  readonly height: number;
  /** 장비 메시 수(드로우콜 예산 보고용) */
  readonly drawCalls: number;
}

const tmpV = new THREE.Vector3();
const tmpV2 = new THREE.Vector3();
const tmpS = new THREE.Vector3();
const tmpQ = new THREE.Quaternion();
const UP = new THREE.Vector3(0, 1, 0);

/** 이름 끝으로 본을 찾는다 */
export function findBone(root: THREE.Object3D, suffix: string): THREE.Bone | null {
  let found: THREE.Bone | null = null;
  const re = new RegExp(suffix + '$');
  root.traverse((o) => { if (!found && (o as THREE.Bone).isBone && re.test(o.name)) found = o as THREE.Bone; });
  return found;
}

/** 무기 소켓: 오른손(Kael 대검), 왼손(Lyra 활), 등(Spine2), 머리 */
export function lyraSockets(root: THREE.Object3D): { rightHand: THREE.Bone | null; leftHand: THREE.Bone | null; spine: THREE.Bone | null; head: THREE.Bone | null } {
  return {
    rightHand: findBone(root, 'RightHand'),
    leftHand: findBone(root, 'LeftHand'),
    spine: findBone(root, 'Spine2') ?? findBone(root, 'Spine1') ?? findBone(root, 'Spine'),
    head: findBone(root, 'Head'),
  };
}

function painted(g: THREE.BufferGeometry, hex: number, bright = 1): THREE.BufferGeometry {
  const src = g.index ? g.toNonIndexed() : g;
  if (src !== g) g.dispose();
  const c = new THREE.Color(hex).multiplyScalar(bright);
  const n = src.getAttribute('position').count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
  src.setAttribute('color', new THREE.BufferAttribute(col, 3));
  src.deleteAttribute('uv');
  return src;
}

function mergeAll(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  let total = 0;
  for (const p of parts) total += p.getAttribute('position').count;
  const pos = new Float32Array(total * 3), nor = new Float32Array(total * 3), col = new Float32Array(total * 3);
  let o = 0;
  for (const p of parts) {
    if (!p.getAttribute('normal')) p.computeVertexNormals();
    pos.set(p.getAttribute('position').array as Float32Array, o * 3);
    nor.set(p.getAttribute('normal').array as Float32Array, o * 3);
    col.set(p.getAttribute('color').array as Float32Array, o * 3);
    o += p.getAttribute('position').count;
    p.dispose();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

/**
 * 장궁(활 공간: 손잡이 = 원점, 활대 = ±Y, 시위 쪽 = -Z, 화살이 나가는 쪽 = +Z). 길이 L(m).
 * 활대: 손잡이에서 위·아래로 뻗다 끝이 앞(+Z)으로 휘는 리커브. 손잡이 감개(은), 활대(어두운 청록 나무 + 청록 줄), 끝 고리(은).
 */
export function buildBowGeometry(L: number): { geometry: THREE.BufferGeometry; tipTop: THREE.Vector3; tipBottom: THREE.Vector3 } {
  const half = L / 2;
  const limbPts = (sign: number): THREE.Vector3[] => [
    new THREE.Vector3(0, sign * 0.06 * L, 0),
    new THREE.Vector3(0, sign * 0.22 * L, -0.03 * L),
    new THREE.Vector3(0, sign * 0.38 * L, -0.045 * L),
    new THREE.Vector3(0, sign * 0.47 * L, -0.02 * L),
    new THREE.Vector3(0, sign * half, 0.035 * L),
  ];
  const parts: THREE.BufferGeometry[] = [];
  for (const sign of [1, -1]) {
    const curve = new THREE.CatmullRomCurve3(limbPts(sign));
    // 활대: 손잡이 쪽이 굵고 끝이 가늘다(TubeGeometry 반경 고정이라 두 겹: 굵은 안쪽 + 가는 바깥 끝)
    parts.push(painted(new THREE.TubeGeometry(curve, 14, 0.016 * L, 6, false), 0x2f5653));
    const inner = new THREE.CatmullRomCurve3(limbPts(sign).slice(0, 3));
    parts.push(painted(new THREE.TubeGeometry(inner, 8, 0.022 * L, 6, false), 0x24403e));
    // 청록 줄(활대 앞면을 따라 가는 빛)
    const glowPts = limbPts(sign).map((p) => p.clone().add(new THREE.Vector3(0, 0, 0.012 * L)));
    parts.push(painted(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(glowPts), 14, 0.008 * L, 4, false), LYRA_TEAL, 1.8));
    // 끝 고리(은)
    const tip = limbPts(sign)[4];
    parts.push(painted(new THREE.SphereGeometry(0.02 * L, 6, 4).translate(tip.x, tip.y, tip.z), LYRA_SILVER, 1.1));
    // 깃 장식(활 끝 바깥으로 짧은 날개)
    parts.push(painted(new THREE.ConeGeometry(0.018 * L, 0.08 * L, 4).rotateX(sign * -0.5).translate(0, tip.y + sign * 0.03 * L, tip.z + 0.02 * L), LYRA_TEAL, 1.2));
  }
  // 손잡이 감개(은)
  parts.push(painted(new THREE.CylinderGeometry(0.026 * L, 0.026 * L, 0.13 * L, 8), LYRA_SILVER, 0.95));
  // 화살 받침(작은 청록 보석)
  parts.push(painted(new THREE.OctahedronGeometry(0.02 * L).translate(0, 0.07 * L, 0.02 * L), LYRA_TEAL, 1.6));
  const top = limbPts(1)[4], bottom = limbPts(-1)[4];
  return { geometry: mergeAll(parts), tipTop: top.clone(), tipBottom: bottom.clone() };
}

/** 화살통(등 공간: 원점 = 통 아래 끝, 통 = +Y). 길이 h(m) */
export function buildQuiverGeometry(h: number): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const r = 0.11 * h;
  parts.push(painted(new THREE.CylinderGeometry(r, r * 0.85, h, 10, 1, true).translate(0, h / 2, 0), 0x5a3f2c));
  parts.push(painted(new THREE.CylinderGeometry(r * 0.88, r * 0.88, 0.03 * h, 10).translate(0, 0.015 * h, 0), 0x5a3f2c));
  // 은 테 두 줄 + 청록 띠
  parts.push(painted(new THREE.TorusGeometry(r * 1.02, 0.018 * h, 4, 14).rotateX(Math.PI / 2).translate(0, h * 0.97, 0), LYRA_SILVER, 1.05));
  parts.push(painted(new THREE.TorusGeometry(r * 0.95, 0.014 * h, 4, 14).rotateX(Math.PI / 2).translate(0, h * 0.2, 0), LYRA_SILVER, 0.95));
  parts.push(painted(new THREE.CylinderGeometry(r * 1.005, r * 0.98, 0.07 * h, 10, 1, true).translate(0, h * 0.62, 0), LYRA_TEAL, 1.2));
  // 화살 다섯 대(깃이 통 위로 나온다)
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3, rr = r * 0.5;
    const x = Math.cos(a) * rr, z = Math.sin(a) * rr, top = h * (1.18 + 0.05 * (i % 2));
    parts.push(painted(new THREE.CylinderGeometry(0.008 * h, 0.008 * h, 0.3 * h, 4).translate(x, top - 0.15 * h, z), LYRA_SILVER, 0.9));
    parts.push(painted(new THREE.BoxGeometry(0.07 * h, 0.12 * h, 0.006 * h).rotateY(a).translate(x, top, z), LYRA_TEAL, 1.3));
    parts.push(painted(new THREE.BoxGeometry(0.006 * h, 0.12 * h, 0.07 * h).rotateY(a).translate(x, top, z), LYRA_SILVER, 1.0));
  }
  return mergeAll(parts);
}

/** 머리 장식(머리 공간: 원점 = 머리 본, 위 = +Y): 은빛 깃 두 개가 뒤로 솟는다 + 청록 보석 */
export function buildOrnamentGeometry(s: number): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (const side of [-1, 1]) {
    const g = new THREE.ConeGeometry(0.03 * s, 0.26 * s, 4).scale(1, 1, 0.35);
    g.rotateX(-0.75).rotateZ(side * 0.35).translate(side * 0.07 * s, 0.17 * s, -0.08 * s);
    parts.push(painted(g, LYRA_SILVER, 1.15));
    const g2 = new THREE.ConeGeometry(0.018 * s, 0.17 * s, 4).scale(1, 1, 0.35);
    g2.rotateX(-0.9).rotateZ(side * 0.55).translate(side * 0.1 * s, 0.13 * s, -0.06 * s);
    parts.push(painted(g2, LYRA_TEAL, 1.3));
  }
  parts.push(painted(new THREE.OctahedronGeometry(0.025 * s).translate(0, 0.13 * s, 0.05 * s), LYRA_TEAL, 1.8));
  return mergeAll(parts);
}

function gearMaterial(): THREE.MeshToonNodeMaterial {
  const m = new THREE.MeshToonNodeMaterial({ vertexColors: true });
  m.emissive = new THREE.Color(0x000000);
  // 정점 색이 1을 넘는 곳(청록 줄·보석)은 스스로 빛난다
  (m as unknown as { emissiveNode: unknown }).emissiveNode = materialColor.rgb.sub(vec3(1, 1, 1)).max(0).mul(0.8);
  return m;
}

function hullMaterial(): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ color: LYRA_OUTLINE, side: THREE.BackSide });
  m.positionNode = positionLocal.add(normalLocal.mul(GEAR_OUTLINE_M));
  return m;
}

/** 소켓: 본 아래에 붙고 본의 월드 배율을 상쇄한다(장비 지오메트리는 월드 m 단위) */
function socket(bone: THREE.Object3D, name: string): THREE.Group {
  const g = new THREE.Group();
  g.name = name;
  bone.updateWorldMatrix(true, false);
  bone.getWorldScale(tmpS);
  g.scale.setScalar(1 / (tmpS.x || 1));
  bone.add(g);
  return g;
}

/** 몸 색조: 진홍(빨강이 압도적이고 초록≈파랑)만 청록으로 돌리고, 피부·가죽·은은 그대로 둔다(텍스처 실측: 진홍 g-b ≈ -0.015, 피부 g-b ≥ 0.05) */
export function lyraTintNode(): THREE.Node<'vec3'> {
  const c = materialColor;
  const red = c.r.sub(max(c.g, c.b));
  const mask = smoothstep(0.1, 0.22, red).mul(float(1).sub(smoothstep(0.0, 0.05, c.g.sub(c.b))));
  const teal = vec3(0.12, 1.0, 0.86).mul(c.r).mul(1.15);
  return mix(c.rgb, teal, mask);
}

/**
 * Kael 루트(KaelModel.root 또는 GLTF scene)에 Lyra 외형을 씌운다. 반환 핸들을 removeLyraLook에 넘기면 원래대로.
 * 같은 루트에 두 번 부르면 앞의 것을 먼저 지운다.
 */
export function applyLyraLook(kaelRoot: THREE.Object3D, o: LyraLookOptions = {}): LyraLook {
  const prev = kaelRoot.userData.lyraLook as LyraLook | undefined;
  if (prev) removeLyraLook(prev);
  kaelRoot.updateWorldMatrix(true, true);
  const sk = lyraSockets(kaelRoot);
  if (!sk.leftHand || !sk.spine || !sk.head) throw new Error('Lyra 외형: 손·등·머리 본을 찾지 못했다');

  // 캐릭터 키(m): 머리 꼭대기 본(없으면 머리 + 15%)과 루트 높이
  const top = findBone(kaelRoot, 'HeadTop_End') ?? sk.head;
  kaelRoot.getWorldPosition(tmpV);
  top.getWorldPosition(tmpV2);
  const height = Math.max(0.5, (tmpV2.y - tmpV.y) * (top === sk.head ? 1.15 : 1));

  const restore: (() => void)[] = [];
  const hidden: { obj: THREE.Object3D; visible: boolean }[] = [];
  // (a) 대검 숨김: 오른손 본 아래 본이 아닌 자식(KaelModel의 검 소켓)
  if (sk.rightHand) for (const ch of sk.rightHand.children) if (!(ch as THREE.Bone).isBone) { hidden.push({ obj: ch, visible: ch.visible }); ch.visible = false; }

  // (b) 색조: 몸(툰)·외곽선 헐·가림 실루엣
  if (o.tint !== false) {
    kaelRoot.traverse((obj) => {
      const m = obj as THREE.Mesh;
      if (!(m as THREE.SkinnedMesh).isSkinnedMesh) return;
      const mat = m.material as THREE.Material & { color?: THREE.Color; colorNode?: unknown };
      if (Array.isArray(mat)) return;
      if ((mat as THREE.MeshToonNodeMaterial).isMeshToonNodeMaterial) {
        const old = mat.colorNode;
        mat.colorNode = lyraTintNode();
        mat.needsUpdate = true;
        restore.push(() => { mat.colorNode = old; mat.needsUpdate = true; });
      } else if (mat.side === THREE.BackSide && mat.color) {
        const old = mat.color.getHex();
        mat.color.setHex(LYRA_OUTLINE);
        restore.push(() => { mat.color!.setHex(old); });
      } else if (mat.depthFunc === THREE.GreaterDepth && mat.colorNode) {
        const old = mat.colorNode;
        const fres = float(1).sub(normalView.dot(positionViewDirection).clamp(0, 1)).pow(3);
        mat.colorNode = mix(vec3(0.25, 0.85, 0.78), vec3(0.93, 0.98, 1.0), step(0.42, fres));
        mat.needsUpdate = true;
        restore.push(() => { mat.colorNode = old; mat.needsUpdate = true; });
      }
    });
  }

  const castShadow = o.castShadow ?? true;
  const mkMesh = (g: THREE.BufferGeometry, name: string, outline: boolean): THREE.Group => {
    const grp = new THREE.Group();
    grp.name = name;
    const mesh = new THREE.Mesh(g, gearMaterial());
    mesh.castShadow = castShadow;
    mesh.receiveShadow = true;
    mesh.renderOrder = 2;
    mesh.name = name + '-body';
    grp.add(mesh);
    if (outline) {
      const hull = new THREE.Mesh(g, hullMaterial());
      hull.renderOrder = 3;
      hull.name = name + '-outline';
      grp.add(hull);
    }
    return grp;
  };

  // (a) 장궁: 왼손
  const sock = BOW_SOCKET[o.body ?? 'kael'];
  const L = height * (o.bowRatio ?? sock.ratio);
  const bowG = o.bowGeometry ? (() => {
    const g = o.bowGeometry!.clone().scale(L / BOW_GLB_LENGTH, L / BOW_GLB_LENGTH, L / BOW_GLB_LENGTH);
    const t = bowTips(L);
    return { geometry: g, tipTop: t.top, tipBottom: t.bottom };
  })() : buildBowGeometry(L);
  const bowSocket = socket(sk.leftHand, 'lyra-bow-socket');
  const rot = o.bowRot ?? sock.rot, pos = o.bowPos ?? sock.pos;
  bowSocket.position.set(pos[0] * (bowSocket.scale.x), pos[1] * bowSocket.scale.x, pos[2] * bowSocket.scale.x);
  if (o.autoAlign) {
    // 소켓 국소 회전 = (본 월드 회전)⁻¹ × (루트 월드 회전)
    const qb = new THREE.Quaternion(), qr = new THREE.Quaternion();
    sk.leftHand.getWorldQuaternion(qb);
    kaelRoot.getWorldQuaternion(qr);
    const a = o.alignEuler ?? [0, 0, 0];
    qr.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler((a[0] * Math.PI) / 180, (a[1] * Math.PI) / 180, (a[2] * Math.PI) / 180)));
    bowSocket.quaternion.copy(qb.invert().multiply(qr));
  } else {
    bowSocket.rotation.set((rot[0] * Math.PI) / 180, (rot[1] * Math.PI) / 180, (rot[2] * Math.PI) / 180);
  }
  const aimQ = bowSocket.quaternion.clone();
  const carryQ = sock.carryRot && !o.bowRot && !o.autoAlign
    ? new THREE.Quaternion().setFromEuler(new THREE.Euler((sock.carryRot[0] * Math.PI) / 180, (sock.carryRot[1] * Math.PI) / 180, (sock.carryRot[2] * Math.PI) / 180, 'XYZ'))
    : aimQ.clone();
  bowSocket.quaternion.copy(carryQ);
  const bow = mkMesh(bowG.geometry, 'lyra-bow', true);
  bowSocket.add(bow);
  // 시위: 끝 둘 → 당김점, 가는 원통 두 개(같은 지오메트리·재질)
  const strGeo = new THREE.CylinderGeometry(0.0035 * L, 0.0035 * L, 1, 4, 1).translate(0, 0.5, 0);
  const strMat = new THREE.MeshBasicNodeMaterial({ color: 0xdff4f2 });
  const s1 = new THREE.Mesh(strGeo, strMat), s2 = new THREE.Mesh(strGeo, strMat);
  s1.name = 'lyra-string-top'; s2.name = 'lyra-string-bottom';
  bow.add(s1, s2);

  // 화살통: 등(Spine2), 오른쪽 어깨 위로 비스듬히
  const quiverSocket = socket(sk.spine, 'lyra-quiver-socket');
  const qh = height * 0.27;
  const quiver = mkMesh(buildQuiverGeometry(qh), 'lyra-quiver', true);
  quiver.position.set(-0.05 * height, -0.24 * height, -0.09 * height);
  quiver.rotation.set(-0.2, 0, -0.45);
  quiverSocket.add(quiver);

  // (c) 머리 장식
  const headSocket = socket(sk.head, 'lyra-ornament-socket');
  const ornament = mkMesh(buildOrnamentGeometry(height * 0.62), 'lyra-ornament', false);
  headSocket.add(ornament);
  if (o.accessories === false) { quiver.visible = false; ornament.visible = false; }

  const look: LyraLook = {
    root: kaelRoot, bow, quiver, ornament, bowSocket, hidden, restore, draw: 0,
    tipTop: bowG.tipTop, tipBottom: bowG.tipBottom, nock: new THREE.Vector3(), strings: [s1, s2], reach: o.drawReach ?? sock.reach, aimQ, carryQ, aim: 0, height,
    drawCalls: o.accessories === false ? 4 : 7,
  };
  kaelRoot.userData.lyraLook = look;
  setLyraDraw(look, 0);
  return look;
}

/**
 * 쏘는 자세 비중(0 들기 ~ 1 쏘기): 활 소켓 회전을 들고 있는 자세와 쏘는 자세 사이로 섞는다. 할당 없음.
 * 렌더가 공격·스킬·각성 중이면 1로 부드럽게 올리고(시위 당김과 달리 화살이 나가도 내리지 않는다) 끝나면 내린다
 */
export function setLyraAim(look: LyraLook, w: number): void {
  const a = w < 0 ? 0 : w > 1 ? 1 : w;
  look.aim = a;
  if (look.aimQ.equals(look.carryQ)) return;
  look.bowSocket.quaternion.slerpQuaternions(look.carryQ, look.aimQ, a * a * (3 - 2 * a));
}

/** 시위 당김 0(쉼) ~ 1(가득): 당김점이 시위 쪽(-Z)으로 활 길이의 reach(카엘 몸 30%, 라이라 몸 56%)까지 물러난다. 할당 없음 */
export function setLyraDraw(look: LyraLook, t: number): void {
  const d = t < 0 ? 0 : t > 1 ? 1 : t;
  look.draw = d;
  const L = look.tipTop.y - look.tipBottom.y;
  // 쉼: 시위가 끝점을 곧게 잇는다(끝 z 평균). 당김: 손잡이 높이에서 뒤로
  look.nock.set(0, 0, (look.tipTop.z + look.tipBottom.z) * 0.5 - look.reach * L * d);
  placeString(look.strings[0], look.tipTop, look.nock);
  placeString(look.strings[1], look.tipBottom, look.nock);
}

function placeString(m: THREE.Mesh, a: THREE.Vector3, b: THREE.Vector3): void {
  tmpV.subVectors(a, b);
  const len = tmpV.length();
  m.position.copy(b);
  m.scale.set(1, Math.max(1e-4, len), 1);
  tmpQ.setFromUnitVectors(UP, tmpV.multiplyScalar(1 / Math.max(1e-6, len)));
  m.quaternion.copy(tmpQ);
}

/** 원래대로: 장비를 떼고 재질 값·대검 표시를 되돌린다. 만든 지오메트리·재질은 dispose */
export function removeLyraLook(look: LyraLook): void {
  for (const f of look.restore) f();
  for (const h of look.hidden) h.obj.visible = h.visible;
  const kill = (g: THREE.Object3D): void => {
    const sock = g.parent;
    g.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    });
    if (sock) { sock.remove(g); sock.parent?.remove(sock); }
  };
  kill(look.bow);
  kill(look.quiver);
  kill(look.ornament);
  if (look.root.userData.lyraLook === look) delete look.root.userData.lyraLook;
}
